/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import { useState } from "react";
import { loginUser, waitForAuthenticatedSession, emitAuthLogin } from "../api/api";
import { logoutUser } from "../api/auth";
import { useNavigate, Link } from "react-router-dom";
import { useTheme } from "@/context/ThemeContext";
import PaxiaLogo from "../branding/logo";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { hydrateFromServer } = useTheme();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setError(null);
    setIsSubmitting(true);
    let sessionStarted = false;

    try {
      await loginUser(email, password);
      sessionStarted = true;
      await waitForAuthenticatedSession();
      emitAuthLogin();
      await hydrateFromServer();
      navigate("/dashboard");
    } catch (err) {
      if (sessionStarted) {
        logoutUser();
        setError("We couldn't establish a stable session. Please try again.");
        return;
      }

      // buildApiError already extracts the user-friendly message into
      // err.message regardless of the backend response shape (string detail,
      // array [{msg}], field errors, etc.).  Reading err.data.detail directly
      // can break when it's an array — use err.message as the single source.
      if (err?.status === 403) {
        setError("Too many failed login attempts. Please try again later.");
      } else {
        const message = err?.message || "Login failed";

        if (message.toLowerCase().includes("not active")) {
          setError("Your account has not been approved yet. Please wait for an administrator to activate your account.");
        } else if (message.toLowerCase().includes("not verified")) {
          setError("Please verify your email before logging in. Check your inbox for the verification link.");
        } else {
          setError(message);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-[#0F1826] font-inter text-white overflow-hidden px-4 sm:px-6">


      {/* Optional overlay */}
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-[#0F1826]/90 to-[#0F1826]/90"></div>

      {/* Login Card */}
      <div className="relative z-20 bg-[#1A2539]/80 backdrop-blur-md p-5 sm:p-8 rounded-xl shadow-lg w-full max-w-[340px] sm:max-w-[384px]">
        {/* Logo and Title */}
        <div className="flex flex-col items-center mb-6 space-y-3">
          <img
            src={PaxiaLogo}
            alt="paxia.ai"
            className="h-14 sm:h-16 w-auto object-contain bg-transparent select-none"
          />
          <h1 className="text-2xl font-bold tracking-tight">paxia.ai</h1>
          <p className="text-sm text-gray-400">Please login to continue</p>
        </div>

        {/* Error */}
        {error && (
          <p className="text-red-400 text-center mt-2 text-sm">{error}</p>
        )}

        {/* Login Form */}
        <form onSubmit={handleLogin} className="mt-4 space-y-4">
          <input
            type="text"
            placeholder="Email or Username"
            className="w-full p-3 rounded-lg bg-[#0F1826]/60 border border-[#2A3954] text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4FA9A1]"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="username"
          />
          <input
            type="password"
            placeholder="Password"
            className="w-full p-3 rounded-lg bg-[#0F1826]/60 border border-[#2A3954] text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4FA9A1]"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#4FA9A1] hover:bg-[#3e938b] transition-colors text-[#0F1826] font-semibold py-2 rounded-lg"
          >
            {isSubmitting ? "Logging in..." : "Login"}
          </button>
        </form>
        <p className="text-center text-sm text-gray-400 mt-4">
          Don't have an account?{" "}
          <Link to="/register" className="text-[#4FA9A1] hover:underline">
            Register
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
